/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { Component, ElementRef, forwardRef, inject, viewChild, input, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import { MatOption, MatSelect } from '@angular/material/select';

import { EditableFieldComponent } from '../editable-field.component';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatFormField } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

export const EDITABLE_EMAIL_INPUT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => EditableEmailInputComponent),
  multi: true,
};

interface EmailForm {
  emailFirstPart: string | null;
  domain: string | null;
}

@Component({
  selector: 'vitamui-common-editable-email-input',
  templateUrl: './editable-email-input.component.html',
  styleUrls: ['./editable-email-input.component.scss'],
  providers: [EDITABLE_EMAIL_INPUT_VALUE_ACCESSOR],
  imports: [CdkOverlayOrigin, FormField, MatFormField, MatSelect, MatOption, MatProgressSpinner, CdkConnectedOverlay],
})
export class EditableEmailInputComponent extends EditableFieldComponent {
  private readonly document = inject<Document>(DOCUMENT);

  readonly select = viewChild<MatSelect>('select');

  constructor() {
    const elementRef = inject(ElementRef);

    super(elementRef);
  }

  private readonly emailModel = signal<EmailForm>({ emailFirstPart: null, domain: null });
  readonly formEmail = form(this.emailModel, (path) => required(path.domain));

  /** Called on user changes only (not when the value is written) */
  protected onEmailChange(changes: Partial<EmailForm>) {
    const { emailFirstPart, domain } = { ...this.emailModel(), ...changes };
    this.control.setValue(emailFirstPart + '@' + domain);
    this.control.markAsDirty();
  }

  readonly domains = input<string[]>(undefined);

  override writeValue(value: any) {
    this.control.reset(value, { emitEvent: false });
    this.originValue = value;
    if (value) {
      const splittedEmail = value.split('@');
      this.emailModel.set({ emailFirstPart: splittedEmail[0] ?? null, domain: splittedEmail[1] ?? null });
    }
  }

  override onClick(target: HTMLElement) {
    if (!this.editMode) {
      return;
    }
    const overlayRef = this.cdkConnectedOverlay().overlayRef;

    // Overlay has same id as the "select" element, suffixed by "-panel"
    const selectOverlay = this.document.querySelector(`#${this.select().id}-panel`) as HTMLElement;
    if (
      this.isInside(target, this.elementRef.nativeElement) ||
      this.isInside(target, overlayRef.hostElement) ||
      this.isInside(target, selectOverlay ? selectOverlay : null)
    ) {
      return;
    }
    this.cancel();
  }
}
